import { Box3, Mesh, Matrix4, Group } from "three";
import { OBB } from "assets/JS/OBB";
import { Robot } from "./Robot";
import { StaticMesh } from "./StaticMesh";
import { CollisionType, IRobotJoints } from "./enums";
import { builder } from "./global";

export function checkCollisionWalls(obj1: StaticMesh | Robot): CollisionType {
    let isCollision = false;
    const box = new Box3().setFromObject(obj1.collisiongroup);
    builder._walls.forEach((wall) => {
        const wallBox = new Box3().setFromObject(wall);
        if (box.intersectsBox(wallBox)) {
            isCollision = true;
            return;
        }
    });
    if (isCollision) return CollisionType.Collision;
    else return CollisionType.NoCollision;
}

export function checkCollision(obj1: StaticMesh | Robot, obj2: StaticMesh | Robot): CollisionType {
    let isCollision = false;
    if (obj1 instanceof StaticMesh) {
        if (obj2 instanceof StaticMesh) {
            const b1 = new Box3().setFromObject(obj1.collisiongroup);
            const b2 = new Box3().setFromObject(obj2.collisiongroup);
            const isCol = b1.intersectsBox(b2);
            if (!isCol) {
                return CollisionType.NoCollision;
            }
            obj2.collisionEnvelop.obb.forEach((element) => {
                if (checkBoxCollision(element, obj1.collisionEnvelop)) {
                    isCollision = true;
                    return;
                }
            });
            if (isCollision) return CollisionType.Collision;
            obj2.collisionEnvelop.sphere.forEach((element) => {
                if (checkSphereCollision(element, obj1.collisionEnvelop)) {
                    isCollision = true;
                    return;
                }
            });
            if (isCollision) return CollisionType.Collision;
        } else {
            const b1 = new Box3().setFromObject(obj1.collisiongroup);
            const b2 = new Box3().setFromObject(obj2.collisiongroup);
            const isCol = b1.intersectsBox(b2);
            if (!isCol) {
                return CollisionType.NoCollision;
            }
            if (checkBoxCollision(obj2.base, obj1.collisionEnvelop)) return CollisionType.Collision;
            const armBox = new Box3().setFromObject(obj2.ArmGroup);
            if (b1.intersectsBox(armBox)) {
                obj2.ArmDecomposition.forEach((element) => {
                    if (checkSphereCollision(element, obj1.collisionEnvelop)) {
                        isCollision = true;
                        return;
                    }
                });
            }
            if (isCollision) return CollisionType.Collision;
        }
    } else {
        if (obj2 instanceof StaticMesh) {
            return checkCollision(obj2, obj1);
        } else if (obj2 instanceof Robot) {
            if (checkBoxBox(obj2.base, obj1.base)) return CollisionType.Collision;
            const arm2Box = new Box3().setFromObject(obj2.ArmGroup);
            const arm1Box = new Box3().setFromObject(obj1.ArmGroup);
            if (checkBoxBox3(obj1.base, arm2Box)) {
                obj2.ArmDecomposition.forEach((element) => {
                    if (checkBoxSphere(obj1.base, element)) {
                        isCollision = true;
                        return;
                    }
                });
            }
            if (isCollision) return CollisionType.Collision;

            if (arm1Box.intersectsBox(arm2Box)) {
                obj2.ArmDecomposition.forEach((element) => {
                    obj1.ArmDecomposition.forEach((element2) => {
                        if (checkSphereSphere(element2, element)) {
                            isCollision = true;
                            return;
                        }
                    });
                    if (isCollision) return;
                });
                if (isCollision) return CollisionType.Collision;
            }
        }
    }
    return CollisionType.NoCollision;
}
function checkBoxCollision(box: Mesh, Collision: { obb: Mesh[]; sphere: Mesh[] }) {
    const obb = box.userData.obb as OBB;

    for (const obb1 of Collision.obb) {
        if (checkBoxBox(box, obb1)) return true;
    }

    for (const sph of Collision.sphere) {
        const bs = getBoundingSphere(sph);
        if (bs == null) return false;

        if (obb.intersectsSphere(bs)) return true;
    }

    return false;
}

function checkSphereCollision(sphere: Mesh, Collision: { obb: Mesh[]; sphere: Mesh[] }) {
    const sph = getBoundingSphere(sphere);
    if (sph == null) return false;

    for (const obb1 of Collision.obb) {
        if ((obb1.userData.obb as OBB).intersectsSphere(sph)) return true;
    }

    for (const sph1 of Collision.sphere) {
        const bs = getBoundingSphere(sph1);
        if (bs == null) return false;
        if (sph.intersectsSphere(bs)) return true;
    }

    return false;
}

function checkBoxBox3(box1: Mesh, box2: Box3) {
    const obb1 = box1.userData.obb as OBB;
    return obb1.intersectsBox3(box2);
}

function checkBoxBox(box1: Mesh, box2: Mesh) {
    const obb1 = box1.userData.obb as OBB;
    const obb2 = box2.userData.obb as OBB;
    return obb1.intersectsOBB(obb2);
}
function checkBoxSphere(box: Mesh, sphere: Mesh) {
    const obb = box.userData.obb as OBB;

    const sph = getBoundingSphere(sphere);
    if (sph == null) return false;
    return obb.intersectsSphere(sph);
}
function checkSphereSphere(sphere1: Mesh, sphere2: Mesh) {
    const sph1 = getBoundingSphere(sphere1);
    if (sph1 == null) return false;
    const sph2 = getBoundingSphere(sphere2);
    if (sph2 == null) return false;

    return sph1.intersectsSphere(sph2);
}
function getBoundingSphere(sphere: Mesh) {
    sphere.geometry.computeBoundingSphere();
    const sph = sphere.geometry.boundingSphere?.clone();
    sph?.applyMatrix4(sphere.matrixWorld);
    return sph;
}

function checkBoxBVHCollision(targetObject: Mesh, collider: Mesh): boolean {
    const transformMatrix = new Matrix4()
        .copy(targetObject.matrixWorld)
        .invert()
        .multiply(collider.matrixWorld); //obj1.group.matrixWorld
    const bb = new Box3();
    if (collider.geometry.boundingBox) {
        bb.copy(collider.geometry.boundingBox);
        const intersect = targetObject.geometry.boundsTree?.intersectsBox(bb, transformMatrix);
        // console.log(bb, intersect);
        if (intersect) return intersect;
    }
    return false;
}

function checkSphereBVHCollision(
    targetObject: Mesh,
    collider: Group,
    isSleepMode = false
): boolean {
    // console.log(targetObject.parent, collider);
    for (let i = 0; i < collider.children.length; i++) {
        const child = collider.children[i];
        // console.log(isSleepMode);
        if (child.userData.joint === IRobotJoints.tip && !isSleepMode) continue;
        const transformMatrix = new Matrix4()
            .copy(targetObject.matrixWorld)
            .invert()
            .multiply(collider.children[i].matrixWorld); //obj1.group.matrixWorld
        if (child instanceof Mesh) {
            child.geometry.computeBoundingSphere();
            const sphere = child.geometry.boundingSphere.clone();
            sphere.applyMatrix4(transformMatrix);
            const intersect = targetObject.geometry.boundsTree?.intersectsSphere(sphere);
            if (intersect) return true;
        }
        // if (intersect) console.log(intersect);
    }
    return false;
}

// obj1 : collider obj , obj2: targetObj (patient or bed)
export function checkBVHCollision(
    obj1: StaticMesh | Robot,
    obj2: StaticMesh | Robot
): CollisionType {
    if (obj1 instanceof StaticMesh) {
        if (obj2 instanceof StaticMesh) {
            // obj1: SM , obj2: SM
            const collider = obj1.collisionEnvelop.obb[0];
            const targetObject = obj2.group.children.find((child) => {
                return child.name == "Bed" || child.name == "Patient";
            });
            // console.log(targetObject);
            if (checkBoxBVHCollision(targetObject as Mesh, collider))
                return CollisionType.Collision;
        } else {
            // obj1: SM , obj2: ROBOT

            const targetObject = obj1.group.children.find((child) => {
                return child.name == "Bed" || child.name == "Patient";
            });
            if (checkBoxBVHCollision(targetObject as Mesh, obj2.base)) {
                // console.log("obj1: SM , obj2: ROBOT");
                // console.log(targetObject.name, " <-> ", obj2.name);
                return CollisionType.Collision;
            }
            if (checkSphereBVHCollision(targetObject as Mesh, obj2.ArmGroup, obj2.isSleepMode)) {
                return CollisionType.Collision;
            }
        }
    } else {
        if (obj2 instanceof StaticMesh) {
            // obj1: ROBOT , obj2: SM
            return checkBVHCollision(obj2, obj1);
            /* const targetObject = obj2.group.children[0];
            for (const _child of obj1.collisiongroup.children) {
                if (_child.name === "Base") {
                    if (checkBoxBVHCollision(targetObject as Mesh, _child as Mesh))
                        return CollisionType.Collision;
                }
                if (_child.name === "ArmGroup") {
                    if (checkSphereBVHCollision(targetObject as Mesh, _child as Group))
                        return CollisionType.Collision;
                }
            } */
        }
    }
    return CollisionType.NoCollision;
}
